import { useEffect, useMemo, useRef, useState } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { editorPagesAtom } from '@/atoms/_editorAtoms'
import { selectedPageAtom } from '@/atoms/editorAtoms'
import {
  imageQualityAtom,
  type ImageQualityInfo,
} from '@/atoms/imageQualityAtoms'
import { calculateImageQuality } from '@/utils/editor/calculateImageQuality'
import { calculateProductPPI } from '@/utils/editor/calculateProductPPI'
import { getImageDimensions } from '@/utils/editor/getImageDimensions'

type OriginalImagePixelSize = { width: number | null; height: number | null }

type UseCheckImageQualityArgs = {
  imageId?: string
  imageUrl: string | null
  displayedBoxWidthPixels: string | number | null | undefined
  displayedBoxHeightPixels: string | number | null | undefined
  cropLeftPixels?: number
  cropRightPixels?: number
  cropTopPixels?: number
  cropBottomPixels?: number
  minimumPixelsPerInchBannerProducts?: number
  minimumPixelsPerInchPaperProducts?: number
  currentEnvironment?: 'editor' | 'preview' | 'pdf' | string
  environmentsToWarnIn?: Array<'editor' | string>
  skipQualityCheck?: boolean
}

type UseCheckImageQualityResult = {
  productPixelsPerInch: number | null
  originalImagePixelSize: OriginalImagePixelSize
  effectivePixelsPerInch: number | null
  printedSizeInches: { width: number | null; height: number | null }
  isBelowPrintQualityThreshold: boolean
  showPrintQualityWarning: boolean
}

/**
 * Image print-quality checker for editor items (primarily `PageImage.js`).
 *
 * PURPOSE: Determines if an image will look good when printed by calculating how many original
 * image pixels will be used per inch of printed output ("effective PPI").
 *
 * RETURNS:
 * - effectivePixelsPerInch: How many original image pixels per printed inch (e.g., 290 PPI)
 * - showPrintQualityWarning: true when effective PPI < threshold (150 for banners, 200 for paper)
 *
 * WHY THIS CALCULATION IS NEEDED:
 * When you place an image in a container, CSS scales it with `background-size: cover`.
 * A small image stretched to fill a large container will look pixelated when printed.
 * This hook calculates exactly how much detail will be available in the final print.
 *
 * DATA SOURCES REQUIRED:
 * 1. Product canvas size: productSize.width/height (pixels) and widthIn/heightIn (inches)
 *    - Example: 840×696 pixels for 8.75×7.25 inches = 96 PPI product
 * 2. Image container size: displayedBoxWidth/Height (pixels on canvas)
 *    - Example: 400×300 pixel container on the canvas
 * 3. Original image dimensions: from getImageDimensions(imageUrl)
 *    - Example: 1200×900 pixel photo
 *
 * CALCULATION STEPS:
 * 1. Product PPI = canvas pixels ÷ physical inches (96 PPI in example)
 * 2. Container printed size = container pixels ÷ product PPI (4.17" × 3.125")
 * 3. CSS cover scale = max(container ÷ original) (max(0.33, 0.33) = 0.33)
 * 4. Used original pixels = container pixels ÷ cover scale (1212 × 909 pixels)
 * 5. Effective PPI = used pixels ÷ printed inches (290 PPI = good quality!)
 *
 * EDGE CASES HANDLED:
 * - Cropping: Reduces visible area but doesn't change the scaling factor
 * - Pintura edits: If image is cropped/edited, uses the smaller edited dimensions
 * - Different aspect ratios: Uses the limiting direction (worse PPI) to be conservative
 *
 * THRESHOLDS:
 * - Banner products: 150 PPI minimum (lower resolution acceptable for large prints)
 * - Paper products: 200 PPI minimum (higher resolution needed for close viewing)
 */
export const useCheckImageQuality = ({
  imageId,
  imageUrl,
  displayedBoxWidthPixels,
  displayedBoxHeightPixels,
  cropLeftPixels = 0,
  cropRightPixels = 0,
  cropTopPixels = 0,
  cropBottomPixels = 0,
  minimumPixelsPerInchBannerProducts = 150,
  minimumPixelsPerInchPaperProducts = 200,
  currentEnvironment,
  environmentsToWarnIn = ['editor'],
  skipQualityCheck = false,
}: UseCheckImageQualityArgs): UseCheckImageQualityResult => {
  const editorPages = useRecoilValue(editorPagesAtom)
  const selectedPage = useRecoilValue(selectedPageAtom)
  const setImageQuality = useSetRecoilState(imageQualityAtom)

  // determine the products page size to derive the product PPI
  const page = editorPages?.[selectedPage - 1]
  const productSize = page?.product?.productSize
  const pageSize = page?.pageProperties?.pageSize

  // determine if this is a banner product
  const firstPage = editorPages?.[0]
  const productType = firstPage?.pageProperties?.product
  const isBannerProduct = useMemo(() => {
    const isDetectedBanner =
      productType?.toLowerCase()?.includes('banner') || false
    return isDetectedBanner
  }, [productType])

  const [productPixelsPerInch, setProductPixelsPerInch] = useState<
    number | null
  >(null)
  const [originalImagePixelSize, setOriginalImagePixelSize] =
    useState<OriginalImagePixelSize>({ width: null, height: null })
  const [effectivePixelsPerInch, setEffectivePixelsPerInch] = useState<
    number | null
  >(null)
  const [calculatedBelowThreshold, setCalculatedBelowThreshold] =
    useState(false)

  // Cache original image dimensions so they don't need to be re-loaded on every resize.
  // The image URL doesn't change during resize — only the box dimensions do.
  const [cachedImageDimensions, setCachedImageDimensions] = useState<{
    width: number
    height: number
  } | null>(null)
  const cachedImageUrlRef = useRef<string | null>(null)

  useEffect(() => {
    if (skipQualityCheck || !imageUrl) {
      setCachedImageDimensions(null)
      cachedImageUrlRef.current = null
      return
    }

    if (imageUrl === cachedImageUrlRef.current) return

    let isCancelled = false
    cachedImageUrlRef.current = imageUrl
    setCachedImageDimensions(null)

    const loadDimensions = async () => {
      try {
        const dimensions = await getImageDimensions(imageUrl)
        if (!isCancelled) {
          setCachedImageDimensions(dimensions)
        }
      } catch {
        if (!isCancelled) {
          setCachedImageDimensions(null)
          cachedImageUrlRef.current = null
        }
      }
    }

    loadDimensions()

    return () => {
      isCancelled = true
    }
  }, [imageUrl, skipQualityCheck])

  const minimumPixelsPerInch = useMemo(() => {
    if (isBannerProduct) {
      return minimumPixelsPerInchBannerProducts || 150
    }
    return minimumPixelsPerInchPaperProducts || 200
  }, [
    isBannerProduct,
    minimumPixelsPerInchBannerProducts,
    minimumPixelsPerInchPaperProducts,
  ])

  const displayedCroppedWidthPixels = useMemo(() => {
    const displayedBoxWidth =
      typeof displayedBoxWidthPixels === 'string'
        ? parseFloat(displayedBoxWidthPixels)
        : displayedBoxWidthPixels
    const displayedCroppedBoxWidth = Math.max(
      0,
      (displayedBoxWidth || 0) - (cropLeftPixels || 0) - (cropRightPixels || 0),
    )
    return displayedCroppedBoxWidth
  }, [displayedBoxWidthPixels, cropLeftPixels, cropRightPixels])

  const displayedCroppedHeightPixels = useMemo(() => {
    const displayedBoxHeight =
      typeof displayedBoxHeightPixels === 'string'
        ? parseFloat(displayedBoxHeightPixels)
        : displayedBoxHeightPixels
    const displayedCroppedBoxHeight = Math.max(
      0,
      (displayedBoxHeight || 0) -
        (cropTopPixels || 0) -
        (cropBottomPixels || 0),
    )
    return displayedCroppedBoxHeight
  }, [displayedBoxHeightPixels, cropTopPixels, cropBottomPixels])

  const isBelowPrintQualityThreshold = useMemo(
    () => !skipQualityCheck && calculatedBelowThreshold,
    [skipQualityCheck, calculatedBelowThreshold],
  )

  useEffect(() => {
    if (skipQualityCheck) {
      setProductPixelsPerInch(null)
      return
    }
    const derivedPixelsPerInch = calculateProductPPI(productSize, pageSize)
    setProductPixelsPerInch(derivedPixelsPerInch)
  }, [productSize, pageSize, skipQualityCheck])

  useEffect(() => {
    let isCancelled = false

    if (skipQualityCheck) {
      setEffectivePixelsPerInch(null)
      setOriginalImagePixelSize({ width: null, height: null })
      setCalculatedBelowThreshold(false)
      return
    }

    const cannotRun =
      !imageUrl ||
      !productPixelsPerInch ||
      !displayedCroppedWidthPixels ||
      !displayedCroppedHeightPixels ||
      !cachedImageDimensions
    if (cannotRun) {
      setEffectivePixelsPerInch(null)
      setOriginalImagePixelSize({ width: null, height: null })
      setCalculatedBelowThreshold(false)
      return
    }

    const run = async () => {
      try {
        const displayedBoxWidth =
          typeof displayedBoxWidthPixels === 'string'
            ? parseFloat(displayedBoxWidthPixels)
            : displayedBoxWidthPixels || 0
        const displayedBoxHeight =
          typeof displayedBoxHeightPixels === 'string'
            ? parseFloat(displayedBoxHeightPixels)
            : displayedBoxHeightPixels || 0

        if (isCancelled) return

        const result = await calculateImageQuality({
          imageUrl,
          displayedBoxWidthPixels: displayedBoxWidth,
          displayedBoxHeightPixels: displayedBoxHeight,
          cropLeftPixels,
          cropRightPixels,
          cropTopPixels,
          cropBottomPixels,
          productPixelsPerInch,
          minimumPixelsPerInch,
          cachedOriginalImageDimensions: cachedImageDimensions,
        })

        if (isCancelled) return

        setOriginalImagePixelSize(result.originalImagePixelSize)
        setEffectivePixelsPerInch(result.effectivePixelsPerInch)
        setCalculatedBelowThreshold(result.isBelowPrintQualityThreshold)
      } catch {
        if (isCancelled) return
        setEffectivePixelsPerInch(null)
        setOriginalImagePixelSize({ width: null, height: null })
        setCalculatedBelowThreshold(false)
      }
    }

    run()

    return () => {
      isCancelled = true
    }
  }, [
    imageUrl,
    displayedBoxWidthPixels,
    displayedBoxHeightPixels,
    cropLeftPixels,
    cropRightPixels,
    cropTopPixels,
    cropBottomPixels,
    displayedCroppedWidthPixels,
    displayedCroppedHeightPixels,
    productPixelsPerInch,
    minimumPixelsPerInch,
    skipQualityCheck,
    cachedImageDimensions,
  ])

  useEffect(() => {
    if (skipQualityCheck) {
      return
    }

    const idAndPPIAvailable = imageId && effectivePixelsPerInch !== null
    if (idAndPPIAvailable) {
      setImageQuality((prevQualities) => {
        const filteredQualities = prevQualities.filter(
          (q) => q.imageId !== imageId,
        )

        const newQualityInfo: ImageQualityInfo = {
          imageId,
          hasQualityIssue: isBelowPrintQualityThreshold,
          effectivePixelsPerInch,
          threshold: minimumPixelsPerInch,
        }

        return [...filteredQualities, newQualityInfo]
      })
    }
  }, [
    imageId,
    effectivePixelsPerInch,
    isBelowPrintQualityThreshold,
    minimumPixelsPerInch,
    setImageQuality,
    skipQualityCheck,
  ])

  useEffect(() => {
    const currentImageId = imageId
    return () => {
      if (currentImageId) {
        setImageQuality((prevQualities) =>
          prevQualities.filter((q) => q.imageId !== currentImageId),
        )
      }
    }
  }, [imageId, setImageQuality])

  const skippedQualityCheckReturnValues = {
    productPixelsPerInch: null,
    originalImagePixelSize: { width: null, height: null },
    effectivePixelsPerInch: null,
    printedSizeInches: { width: null, height: null },
    isBelowPrintQualityThreshold: false,
    showPrintQualityWarning: false,
  }

  if (skipQualityCheck) {
    return skippedQualityCheckReturnValues
  }

  const finalShowPrintQualityWarning =
    !skipQualityCheck &&
    effectivePixelsPerInch !== null &&
    isBelowPrintQualityThreshold &&
    environmentsToWarnIn.includes(currentEnvironment || 'unknown')

  return {
    productPixelsPerInch,
    originalImagePixelSize,
    effectivePixelsPerInch,
    printedSizeInches: {
      width: productPixelsPerInch
        ? displayedCroppedWidthPixels / productPixelsPerInch
        : null,
      height: productPixelsPerInch
        ? displayedCroppedHeightPixels / productPixelsPerInch
        : null,
    },
    isBelowPrintQualityThreshold,
    showPrintQualityWarning: finalShowPrintQualityWarning,
  }
}
